import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { useBusinessInfo } from "@/api/queries";
import { useTenantBranding } from "@/api/queries/branding";
import { usePublicLegalIndex } from "@/api/queries/legal";
import { BrandLogo } from "@/components/BrandLogo";
import { TenantBrandMark } from "@/components/TenantBrandMark";
import { resolveI18nText } from "@/components/admin/I18nField";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { openCookieSettings } from "@/lib/consent";
import { buildIngotDocsUrl, buildPlatformUrl } from "@/shell/resolveShell";
import { readyFeaturePages } from "@/marketing/featurePages";
import {
  isPageVisible,
  PUBLIC_PAGES,
  publicPageHref,
} from "@/shell/toggleablePages";
import { useShellNav } from "@/shell/useShellNav";

/**
 * Site-wide footer.
 *
 * Per-shell column composition (matches Header's per-shell nav so
 * marketing footer doesn't link into 404'ing buyer routes):
 *   tenant         → Brand · Produkt · Zákazník
 *   platform       → Brand · Produkt · Pro byznys · Společnost
 *   platform-admin → Brand only (admin staff don't need marketing
 *                    columns; AdminLayout has its own chrome anyway,
 *                    but App.tsx only skips it under /admin/*)
 *
 * Právní odkazy jsou VÝHRADNĚ ve spodním pásu u copyrightu. Dřív se
 * vykreslovaly i ve sloupci „Společnost", ze stejného pole — patička
 * tak nesla každý dokument dvakrát. Na tenant shellu tím sloupec
 * „Společnost" přišel o jediný obsah, který měl, a odpadl celý.
 *
 * Brand block carries the wordmark, tagline and a monospace
 * IČO/DIČ + address line. Reads from `useBusinessInfo()` so the
 * admin owns the legal copy, and from `useTenantBranding()` for the
 * tagline — which is platform-branding data, not a compiled string,
 * and is therefore absent on tenant subdomains.
 */
export function Footer(): JSX.Element {
  const { t } = useTranslation("common");
  const { t: tSaas } = useTranslation("saas");
  const { t: tLegal } = useTranslation("legal");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;
  const business = useBusinessInfo();
  const b = business.data;
  const shellNav = useShellNav();
  // Právní odkazy se generují z publikovaných dokumentů daného shellu —
  // pevný seznam by na obchodu tenanta odkazoval na dokumenty platformy.
  const legalIndex = usePublicLegalIndex(lang);
  const legalLinks = legalIndex.data?.items ?? [];
  // Both shells use branding for page visibility; the tenant also uses its logo.
  const tenantBranding = useTenantBranding({
    enabled: shellNav.shell === "tenant" || shellNav.shell === "platform",
  });
  const visibility = tenantBranding.data?.pages_visibility;
  // Tenant footer links come from the public page registry (KAN-1542),
  // minus the pages this tenant switched off.
  const tenantFooterLinks = (column: "product" | "customer") =>
    PUBLIC_PAGES.flatMap((page) =>
      "footer" in page &&
      page.footer.column === column &&
      isPageVisible(visibility, page.key)
        ? [
            <Link
              key={page.key}
              to={publicPageHref(lang, page.key)}
              className={linkCls}
            >
              {t(page.footer.labelKey)}
            </Link>,
          ]
        : [],
    );

  // Platform claim under the wordmark. Server-owned
  // (``platform_branding.web_tagline_*``, edited in Platform admin →
  // Branding) and served only on the platform host — a tenant
  // storefront renders no tagline rather than ours.
  // KAN-1713 — jeden slogan: dřív tu stál přeložený řetězec a claim
  // z Brandingu se nikde nezobrazoval.
  const tagline =
    shellNav.shell === "platform" ? (tenantBranding.data?.platform_tagline ?? null) : null;

  // KAN-1035 — identita prodávajícího se kreslí jen z toho, co tenant
  // OPRAVDU vyplnil. Dokud profil firmy nevyplní, vrací BE od KAN-1035
  // prázdná pole (first-boot výplň se ven nevydává) a patička je
  // vynechá; dřív tu stál pevný řetězec „Provozovatel: Forgmatic
  // s.r.o., IČ: 00000000" — jméno operátora platformy a neexistující
  // IČO na obchodu tenanta. Radši nic než tvrzení, které není pravda.
  // KAN-1935 — země sídla jménem z platformního katalogu, ve
  // jazyce stránky („Česká republika" / „Czech Republic").
  const countryName = resolveI18nText(b?.country_name_i18n, lang);
  const addressLine = [
    b?.street,
    [b?.postal_code, b?.city].filter(Boolean).join(" "),
    countryName,
  ]
    .filter(Boolean)
    .join(", ");
  const idLine = [
    b?.ico ? `${t("footer.companyId")}: ${b.ico}` : "",
    b?.dic ? `${t("footer.vatId")}: ${b.dic}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const operatorLines = [b?.legal_name ?? "", addressLine, idLine].filter(
    (line): line is string => Boolean(line),
  );
  // KAN-1935 — kontaktní e-mail, na který odkazují Zásady i GDPR
  // („uvedený v patičce"). Jen u vyplněné identity: first-boot výplň
  // nese ``orders@example.com``, a ta se vydat nesmí.
  const contactEmail = b?.legal_name ? b.support_email : "";

  const linkCls = "text-ink-3 transition-colors hover:text-ink";

  // Public status page, hosted off our own infrastructure (a status
  // page served by the thing it reports on is dark exactly when people
  // open it). Optional by design: a deployment without one renders no
  // link rather than advertising a page that doesn't exist.
  const statusUrl = import.meta.env.VITE_STATUS_URL;

  // Pricing href differs by shell (matches Header):
  //   tenant   → /pricing   (per-end-customer material pricing)
  //   platform → /pricing-plans (SaaS plan tiers)
  const pricingHref =
    shellNav.shell === "platform"
      ? `/${lang}/pricing-plans`
      : `/${lang}/pricing`;

  // Column count per shell governs the grid template. platform-admin
  // collapses to a brand-only row (no marketing/buyer link makes
  // sense there).
  const gridCls = (() => {
    const base = "mx-auto grid max-w-6xl gap-10 px-7 pb-10 pt-14";
    switch (shellNav.shell) {
      case "platform-admin":
        return base;
      case "platform":
        // Brand · Produkt · Pro byznys · Společnost
        return `${base} md:grid-cols-[1.6fr_1fr_1fr_1fr]`;
      case "tenant":
      default:
        // Brand · Produkt · Zákazník. Sloupec „Společnost" na obchodu
        // tenanta odpadl s právními odkazy — ty jsou ve spodním pásu.
        return `${base} md:grid-cols-[1.6fr_1fr_1fr]`;
    }
  })();

  return (
    <footer className="border-t border-border bg-bg">
      <div className={gridCls}>
        {/* Brand block */}
        <div>
          <div className="mb-4 inline-flex text-ink">
            {shellNav.shell === "tenant" ? (
              <TenantBrandMark
                branding={tenantBranding.data}
                showPoweredBy
                logoClassName="h-8 max-w-[180px]"
              />
            ) : (
              <BrandLogo />
            )}
          </div>
          {tagline && (
            <p
              className="max-w-[300px] text-[13px] leading-relaxed text-ink-3"
              data-testid="footer-tagline"
            >
              {tagline}
            </p>
          )}
          {/* KAN-35 — ukázkový obchod platformy se k tomu hlásí a vede
              na apex. Podle platformního příznaku, ne podle slugu. */}
          {shellNav.shell === "tenant" && tenantBranding.data?.is_platform_demo && (
            <a
              href={buildPlatformUrl(`/${lang}`)}
              className={`mt-3 inline-block text-[13px] ${linkCls}`}
              data-testid="footer-platform-demo"
            >
              {t("footer.platformDemo")}
            </a>
          )}
          {operatorLines.length > 0 && (
            <p
              className="mono mt-4 text-[11px] leading-relaxed text-ink-3"
              data-testid="footer-operator"
            >
              {operatorLines.map((line, i) => (
                <span key={line}>
                  {i > 0 && <br />}
                  {line}
                </span>
              ))}
              {contactEmail && (
                <>
                  <br />
                  <a
                    href={`mailto:${contactEmail}`}
                    className={linkCls}
                    data-testid="footer-email"
                  >
                    {contactEmail}
                  </a>
                </>
              )}
            </p>
          )}
        </div>

        {/* A column whose every page is switched off („Minimální web",
            „Jen konfigurátor") drops with its heading (KAN-1635). */}
        {shellNav.shell === "tenant" &&
          (["product", "customer"] as const).map((column) => {
            const links = tenantFooterLinks(column);
            return links.length > 0 ? (
              <FooterColumn
                key={column}
                label={t(column === "product" ? "footer.productCol" : "footer.customerCol")}
              >
                {links}
              </FooterColumn>
            ) : null;
          })}

        {shellNav.shell === "platform" && (
          <>
            {/* Sekce „Co umíme" (KAN-1462). Vlastní sloupec, ne položky
                v „Produktu": je to pět stránek a v patičce, kde se
                skenuje očima, se pět odkazů mezi ostatní nevejde.
                Vykreslí se jen stránky, které mají routu — seznam a
                důvod jsou ve ``featurePages.ts``. */}
            <FooterColumn
              label={tSaas("features.hub.eyebrow")}
              dataTestid="footer-col-features"
            >
              <Link
                to={`/${lang}/features`}
                className={linkCls}
                data-testid="footer-features"
              >
                {/* Not the hub's H1: a slogan as the first link of a
                    column reads as a heading, not as a page (audit #61). */}
                {tSaas("features.hub.overview")}
              </Link>
              {readyFeaturePages()
                .filter((page) => page.to)
                .map((page) => (
                  <Link
                    key={page.testId}
                    to={`/${lang}${page.to}`}
                    className={linkCls}
                    data-testid={`footer-${page.testId}`}
                  >
                    {tSaas(`features.tiles.${page.i18nKey}.title`)}
                  </Link>
                ))}
            </FooterColumn>

            <FooterColumn label={t("footer.productCol")}>
              <Link
                to={`/${lang}/how-it-works`}
                className={linkCls}
                data-testid="footer-how-it-works"
              >
                {t("footer.howItWorks")}
              </Link>
              <Link
                to={pricingHref}
                className={linkCls}
                data-testid="footer-pricing"
              >
                {t(shellNav.shell === "platform" ? "nav.plans" : "nav.pricing")}
              </Link>
              {/* Srovnání s tržišti. Patří k produktu, ne k důvěře:
                  odpovídá na „proč vlastní obchod", ne na „co s daty". */}
              <Link
                to={`/${lang}/vs-marketplaces`}
                className={linkCls}
                data-testid="footer-vs-marketplaces"
              >
                {t("footer.vsMarketplaces")}
              </Link>
              {/* Jen platform shell — changelog vydává platforma o sobě.
                  Na shopu tenanta by prozradil, že běží na cizí
                  platformě, a jeho zákazníka stejně nezajímá. */}
              <Link
                to={`/${lang}/changelog`}
                className={linkCls}
                data-testid="footer-changelog"
              >
                {t("changelog.heading", "Novinky")}
              </Link>
              {/* Doc web návrhového systému. Jiný HOST (``ingot.<apex>``,
                  vlastní rollup input — KAN-581), proto ``<a>`` a nová
                  karta, ne ``<Link>``: router téhle aplikace tam žádnou
                  routu nemá a klik uvnitř SPA by skončil na 404.

                  Jen platformní shell, ze stejného důvodu jako changelog
                  o řádek výš — na obchodu tenanta by prozradil, na čí
                  platformě běží, a jeho zákazníka nezajímá. */}
              <a
                href={buildIngotDocsUrl()}
                target="_blank"
                rel="noreferrer"
                className={linkCls}
                data-testid="footer-ingot"
              >
                {t("footer.ingot")}
              </a>
            </FooterColumn>

            <FooterColumn
              label={tSaas("nav.forBusinessGroup")}
              dataTestid="footer-col-business"
            >
              <Link
                to={`/${lang}/for-business`}
                className={linkCls}
                data-testid="footer-for-business"
              >
                {t("platformBuyerNav")}
              </Link>
            </FooterColumn>
          </>
        )}

        {/* Právní odkazy tenhle sloupec ZÁMĚRNĚ nenese — bydlí ve
            spodním pásu u copyrightu a dřív se vykreslovaly na obou
            místech z téhož pole, takže patička nesla každý dokument
            dvakrát.

            Zbyly tím věci, které existují jen na platformním shellu
            (Kontakt má tenant ve sloupci „Zákazník", stavová stránka
            hlásí naši infrastrukturu). Na obchodu tenanta by tedy
            sloupec zůstal prázdný — proto se tam nevykreslí vůbec,
            místo nadpisu bez obsahu. */}
        {shellNav.shell === "platform" && (
          <FooterColumn label={t("footer.legalCol")}>
            <Link to={`/${lang}/contact`} className={linkCls}>
              {t("footer.contact")}
            </Link>
            {/* Odpovědi na „kde mám data" a „co když vám to spadne".
                Sousedí se stavovou stránkou schválně — je to táž
                otázka, jednou obecně a jednou právě teď. */}
            <Link
              to={`/${lang}/security`}
              className={linkCls}
              data-testid="footer-security"
            >
              {t("footer.security")}
            </Link>
            {/* Stavová stránka hlásí služby platformy; zákazníkům
                tenanta odpovídá na otázku, kterou nepoložili. */}
            {statusUrl && (
              <a
                href={statusUrl}
                target="_blank"
                rel="noreferrer"
                className={linkCls}
                data-testid="footer-status"
              >
                {t("footer.status")}
              </a>
            )}
          </FooterColumn>
        )}
      </div>

      {/* Copyright + legal strip */}
      <div className="border-t border-border">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-7 py-5 text-xs text-ink-3">
          <p>
            {/* KAN-1644 — jméno obchodu = jméno firmy (``branding.name`` ho
                na obchodě čte z firemních údajů, na platformním hostu nese
                jméno platformy). Nevyplněná identita = bez jména, žádná
                náhrada. Tečka jen tam, kde jméno samo nekončí tečkou
                („s.r.o." → dřív „s.r.o.."). */}
            © {new Date().getFullYear()} {copyrightHolder(b?.legal_name || tenantBranding.data?.name)}
            {t("footer.copyrightSuffix")}
          </p>
          {/* JEDINÉ místo, kde právní odkazy v patičce jsou. Dřív tu byl
              napevno i odkaz na /gdpr, který na obchodu tenanta vedl na
              přehled práv PLATFORMY — práva zákazníka tenanta popisují
              jeho zásady ochrany údajů, ne cizí stránka.

              Odkazy se generují z publikovaných dokumentů TOHOTO shellu:
              na obchodu tenanta tak „Obchodní podmínky" vedou na jeho
              podmínky, a reklamační řád s poučením o odstoupení se
              objeví samy, jakmile jsou zveřejněné. Dokud API neodpoví,
              drží se dosavadní dvojice odkazů — patička nesmí zmizet
              kvůli jednomu pomalému dotazu. */}
          <div className="mono flex flex-wrap gap-4">
            {legalLinks.length > 0 ? (
              legalLinks.map((doc) => (
                <Link
                  key={doc.kind_key}
                  to={`/${lang}/${doc.slug}`}
                  className="hover:text-ink"
                  data-testid={`footer-legal-${doc.slug}`}
                >
                  {doc.title}
                </Link>
              ))
            ) : (
              <>
                <Link
                  to={`/${lang}/privacy`}
                  className="hover:text-ink"
                  data-testid="footer-legal-privacy"
                >
                  {t("footer.privacy")}
                </Link>
                <Link
                  to={`/${lang}/terms`}
                  className="hover:text-ink"
                  data-testid="footer-legal-terms"
                >
                  {t("footer.terms")}
                </Link>
              </>
            )}
            {/* KAN-125 — do teď na tuhle stránku nikdo nelinkoval:
                odkazy výš se generují z ``legal_document_kinds``, kam
                seznam zpracovatelů nepatří (není to verzovaný dokument
                se souhlasy). Odkazovaly na ni jen zásady ochrany údajů
                ve svém těle, což je na povinnou přílohu podle čl. 28
                málo.

                Jen platformní shell: seznam popisuje zpracovatele
                PLATFORMY. Na obchodu tenanta by vedle jeho vlastních
                podmínek tvrdil, že tohle jsou jeho dodavatelé. */}
            {shellNav.shell === "platform" && (
              <Link
                to={`/${lang}/legal/subprocessors`}
                className="hover:text-ink"
                data-testid="footer-legal-subprocessors"
              >
                {tLegal("subprocessors.title")}
              </Link>
            )}
            {/* KAN-1939 — Zásady cookies slibují, že volbu jde „kdykoli
                změnit v nastavení cookies v patičce". Tlačítko, ne odkaz:
                nikam nevede, jen znovu otevře cookie lištu. Na každém
                shellu, protože lišta visí na každém. */}
            <button
              type="button"
              onClick={openCookieSettings}
              className="hover:text-ink"
              data-testid="footer-cookie-settings"
            >
              {t("footer.cookieSettings")}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({
  label,
  children,
  dataTestid,
}: {
  label: string;
  children: React.ReactNode;
  dataTestid?: string;
}): JSX.Element {
  return (
    <div data-testid={dataTestid}>
      <p className="mb-3.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink">
        {label}
      </p>
      <div className="flex flex-col gap-2 text-[13px]">{children}</div>
    </div>
  );
}

/** „Jméno. " pro řádek copyrightu — bez zdvojené tečky, prázdné bez jména. */
function copyrightHolder(name: string | null | undefined): string {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return "";
  return trimmed.endsWith(".") ? `${trimmed} ` : `${trimmed}. `;
}
